import v2 from './V2';
import {fromString} from '../ToastMessage';

let captchaReady;
const submittingForms = new WeakSet();
const verifiedForms = new WeakSet();

export const initializeCaptcha = root => {
    const captchaInput = root.querySelector('[data-google-recaptcha-v3]');

    if (!captchaInput || captchaReady) {
        return;
    }

    captchaReady = new Promise((resolve, reject) => {
        const googleCaptcha = document.createElement('script');
        googleCaptcha.src = `https://www.google.com/recaptcha/enterprise.js?render=${captchaInput.dataset.googleRecaptchaSiteKey}&badge=bottomleft`;
        googleCaptcha.onload = () => grecaptcha.enterprise.ready(() => resolve(true));
        googleCaptcha.onerror = reject;
        document.body.appendChild(googleCaptcha);
    }).catch(() => false);
};

export const handleCaptchaSubmit = async event => {
    const form = event.target;
    const captchaInput = form.querySelector('[data-google-recaptcha-v3]');

    if (!captchaInput || event.defaultPrevented) {
        return;
    }

    if (verifiedForms.has(form)) {
        verifiedForms.delete(form);
        return;
    }

    event.preventDefault();

    if (submittingForms.has(form)) {
        return;
    }

    form.querySelector('[data-captcha-error]').hidden = true;
    submittingForms.add(form);
    form.setAttribute('aria-busy', 'true');

    const pendingButton = Array.from(form.elements).find(element => element.matches('[data-captcha-pending-label]'));
    const buttonLabel = pendingButton?.textContent;

    if (pendingButton) {
        pendingButton.disabled = true;
        pendingButton.textContent = pendingButton.dataset.captchaPendingLabel;
    }

    try {
        if (!await captchaReady) {
            captchaReady = undefined;
            initializeCaptcha(form);
            throw new Error('CAPTCHA script unavailable');
        }

        if (!form.isConnected) {
            return;
        }

        captchaInput.value = await grecaptcha.enterprise.execute(captchaInput.dataset.googleRecaptchaSiteKey, {
            action: formatAction(new URL(form.action).pathname),
        });

        if (!form.isConnected) {
            return;
        }

        const response = await fetch('/captcha/v3', {method: 'post', body: new FormData(form)});
        const responseBody = await response.json();

        if (!form.isConnected) {
            return;
        }

        if (response.status === 400) {
            fromString(responseBody.html);

            return;
        }

        if (responseBody.captchaRequired) {
            if (form.nextElementSibling?.matches('form[action="/captcha/v2"]')) {
                grecaptcha.enterprise.reset();

                return;
            }

            renderV2(form, responseBody.html);

            return;
        }

        setPass(form, responseBody.captchaPass);
    } catch {
        form.querySelector('[data-captcha-error]').hidden = false;
        return;
    } finally {
        submittingForms.delete(form);
        form.removeAttribute('aria-busy');

        if (pendingButton) {
            pendingButton.disabled = false;
            pendingButton.textContent = buttonLabel;
        }
    }

    submitVerified(form);
};

function submitVerified(form) {
    let submissionEvent;
    const observeSubmission = event => { submissionEvent = event; };

    form.addEventListener('submit', observeSubmission, true);
    verifiedForms.add(form);
    try {
        form.requestSubmit();
    } finally {
        form.removeEventListener('submit', observeSubmission, true);
        verifiedForms.delete(form);
    }

    if (submissionEvent && !submissionEvent.defaultPrevented) {
        submittingForms.add(form);
        form.setAttribute('aria-busy', 'true');
        const button = Array.from(form.elements).find(element => element.matches('[data-captcha-pending-label]'));
        if (button) {
            button.disabled = true;
            button.textContent = button.dataset.captchaPendingLabel;
        }
    }
}

function setPass(form, pass) {
    const passInput = form.elements.namedItem('captchaPass');

    if (passInput) {
        passInput.value = pass ?? '';
    }
}

function formatAction(action) {
    return action.replace(/(?:^\w|[A-Z]|\b\w)/g, function(word, index) {
        return index === 0 ? word.toLowerCase() : word.toUpperCase();
    }).replace(/\s+/g, '').replace(/-/g, '');
}

function renderV2(form, html) {
    form.insertAdjacentHTML('afterend', html);

    const v2Form = form.nextElementSibling;

    v2(v2Form, async token => {
        if (!form.isConnected) {
            return;
        }

        try {
            v2Form.querySelector('input').value = token;

            const body = new FormData(v2Form);
            body.set('emailAddress', form.elements.namedItem('emailAddress').value);

            const response = await fetch('/captcha/v2', {method: 'post', body});
            const responseBody = await response.json();

            if (!form.isConnected) {
                return;
            }

            if (responseBody.captchaRequired) {
                grecaptcha.enterprise.reset();

                return;
            }

            setPass(form, responseBody.captchaPass);
            submitVerified(form);
        } catch {
            form.querySelector('[data-captcha-error]').hidden = false;
        }
    });
}
